import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { bondReadyAPI } from '../../services/api';
import CfoInsightsShell from './CfoInsightsShell';
import { resolveOrgLabelsFromAccess } from './orgLabels';
import {
  buildContractorPortfolioRow,
  partitionContractorPortfolioRows,
} from './contractorRequestPortfolioModel';

function RequestRow({ row }) {
  return (
    <article
      className="rounded-xl border border-slate-800 bg-slate-900/70 p-4"
      data-testid={`contractor-bondready-row-${row.opportunityId}`}
      data-bucket={row.bucket}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h3 className="text-base font-semibold text-slate-50">{row.projectLabel}</h3>
          <p className="text-sm text-slate-300">
            {row.requestTypeLabel}
            {row.partnerLabel ? ` \u00B7 ${row.partnerLabel}` : ''}
          </p>
          <p className="text-sm font-medium text-sky-300">{row.statusLabel}</p>
          <p className="text-sm text-slate-300">
            Current owner: {row.currentOwner}
          </p>
          <p className="text-sm text-slate-300">
            Next action: {row.nextAction}
          </p>
          {row.yourActionCopy ? (
            <p className="text-sm text-amber-200" data-testid="contractor-bondready-your-action">
              {row.yourActionCopy}
            </p>
          ) : null}
          {row.outstandingInfoCount > 0 ? (
            <p className="text-xs text-slate-400" data-testid="contractor-bondready-info-count">
              {row.outstandingInfoCount} outstanding information request
              {row.outstandingInfoCount === 1 ? '' : 's'}
            </p>
          ) : null}
          {row.updatedLabel ? (
            <p className="text-xs text-slate-500">{row.updatedLabel}</p>
          ) : null}
        </div>
        {row.detailPath ? (
          <a
            href={row.detailPath}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-500"
            data-testid={`contractor-bondready-open-${row.opportunityId}`}
          >
            Open request
          </a>
        ) : null}
      </div>
    </article>
  );
}

function Section({ title, subtitle, testId, rows, empty }) {
  return (
    <section className="space-y-3" data-testid={testId}>
      <div>
        <h2 className="text-lg font-semibold text-slate-50">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-slate-400">{subtitle}</p> : null}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-800 px-4 py-6 text-sm text-slate-500">
          {empty}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <RequestRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Contractor Sub View destination for BondReady requests.
 * Uses canonical /v1/bondready opportunities for the authenticated org only.
 * Does not change Holmes partner portfolio behavior at /bondready.
 */
export default function ContractorBondReadyRequestsPage() {
  const navigate = useNavigate();
  const [orgAccess, setOrgAccess] = useState(undefined);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const { displayName: orgDisplayName, shortName: orgShortName } =
    resolveOrgLabelsFromAccess(orgAccess || null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get('/auth/organization-access');
        if (!cancelled) setOrgAccess(resp.data || null);
      } catch {
        if (!cancelled) setOrgAccess(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (orgAccess === undefined) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [listResponse, partnerResponse] = await Promise.all([
          bondReadyAPI.listOpportunities(),
          bondReadyAPI.getConfiguredBondPartner().catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const listed = Array.isArray(listResponse.data) ? listResponse.data : [];
        // Fail closed for contractor view: only OWNER-visible opportunities.
        const owned = listed.filter((item) => item?.viewerRole === 'OWNER');
        const partnerDisplayName = partnerResponse?.data?.partnerDisplayName || null;
        const resolvedOrgName = resolveOrgLabelsFromAccess(orgAccess).displayName;
        const callerOrgId = orgAccess?.organizationId || orgAccess?.organization?.id || null;

        const built = [];
        for (const listedItem of owned) {
          if (
            callerOrgId
            && listedItem?.owningOrganizationId
            && String(listedItem.owningOrganizationId) !== String(callerOrgId)
          ) {
            continue;
          }

          let detail = listedItem;
          try {
            const detailResponse = await bondReadyAPI.getOpportunity(listedItem.id);
            detail = detailResponse?.data || listedItem;
          } catch {
            detail = listedItem;
          }
          if (detail?.viewerRole && detail.viewerRole !== 'OWNER') continue;
          if (
            callerOrgId
            && detail?.owningOrganizationId
            && String(detail.owningOrganizationId) !== String(callerOrgId)
          ) {
            continue;
          }

          const requests = Array.isArray(detail.requests) ? detail.requests : [];
          for (const request of requests) {
            let informationRequests = [];
            try {
              const infoResponse = await bondReadyAPI.listInformationRequests(
                detail.id,
                request.id
              );
              informationRequests = Array.isArray(infoResponse.data) ? infoResponse.data : [];
            } catch {
              informationRequests = [];
            }
            const row = buildContractorPortfolioRow({
              opportunity: detail,
              request,
              informationRequests,
              partnerDisplayName,
              orgDisplayName: resolvedOrgName,
            });
            if (row) built.push(row);
          }
        }
        if (!cancelled) {
          setRows(built);
          setError(null);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(
            caught?.response?.data?.error
              || caught?.message
              || 'Unable to load BondReady requests.'
          );
          setRows([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgAccess]);

  const partitions = useMemo(
    () => partitionContractorPortfolioRows(rows || []),
    [rows]
  );

  return (
    <CfoInsightsShell
      orgDisplayName={orgDisplayName}
      orgShortName={orgShortName}
      activeTabId={null}
      onTabClick={(id) => navigate(`/cfo-insights?tab=${id}`)}
    >
      <div className="space-y-6" data-testid="contractor-bondready-portfolio">
        <header>
          <h1 className="text-2xl font-semibold text-slate-50">BondReady</h1>
          <p className="mt-1 text-sm text-slate-400">
            Current bond and capital requests for {orgDisplayName}. Open a request to continue in the
            canonical BondReady tracker.
          </p>
        </header>

        {error ? (
          <div role="alert" className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        ) : null}

        {rows == null ? (
          <div className="text-sm text-slate-400">Loading BondReady requests�</div>
        ) : (
          <>
            <Section
              title="Needs your attention"
              subtitle="Requests where your organization owns the next action."
              testId="contractor-bondready-needs-attention"
              rows={partitions.needsAttention}
              empty="Nothing needs your attention right now."
            />
            <Section
              title="Waiting on partner"
              subtitle="Active requests waiting on Holmes or another authorized partner."
              testId="contractor-bondready-waiting"
              rows={partitions.waitingOnPartner}
              empty="No requests are waiting on a partner."
            />
            <Section
              title="Completed / closed"
              subtitle="Finished requests kept separate from actionable work."
              testId="contractor-bondready-completed"
              rows={partitions.completed}
              empty="No completed requests yet."
            />
          </>
        )}
      </div>
    </CfoInsightsShell>
  );
}
